import React, { useCallback, useState } from 'react'
import { Table } from 'shared/ui/Table/Table'
import { useIdNavigation } from './utility'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  updatingIdAtom,
  listDataTitleAtom,
  resourceKeyAtom,
  isAddingAtom,
  addingDisabledAtom,
  listDataAtom,
  mutationFieldsAtom,
} from './atoms'
import { useTableColumns, useSortableColumns } from './tableUtility'
import { Button, TextField } from '@mui/material'
import { NEW_ITEM_ID } from './types'
import { useAtomWithErrorNotification } from 'shared/ui/GlobalDialog'

export function EditableResourceList({
  navigateRows = true,
}: {
  navigateRows?: boolean
}) {
  const resourceKey = useAtomValue(resourceKeyAtom)
  const data =
    useAtomWithErrorNotification(
      { errorTitle: `Error Retrieving ${resourceKey}` },
      listDataAtom,
    ) ?? []
  const title = useAtomValue(listDataTitleAtom)
  const columns = useTableColumns()
  const editingId = useAtomValue(updatingIdAtom)
  const isAdding = useAtomValue(isAddingAtom)

  const idNavigate = useIdNavigation(resourceKey)
  const mutationFields = useAtomValue(mutationFieldsAtom)

  const [searchTerm, setSearchTerm] = useState('')

  const searchableKeys = (mutationFields ?? [])
    .filter((f) => f.valueType === 'string')
    .map((f) => f.key)

  const { columns: sortableColumns, sorted } = useSortableColumns(data, columns)

  const trimmed = searchTerm.trim().toLowerCase()
  const filtered = !trimmed
    ? sorted
    : sorted.filter((item) => {
        const record = item as Record<string, unknown>
        return searchableKeys.some((key) => {
          const val = record[key]
          return typeof val === 'string' && val.toLowerCase().includes(trimmed)
        })
      })

  const doOnRowClick = useCallback(
    (index: number) => {
      if (isAdding) return
      if (editingId || !filtered[index]) return
      idNavigate(filtered[index])
    },
    [idNavigate, filtered, editingId, isAdding],
  )

  return (
    <Table
      title={`${title}: ${filtered.length}`}
      columns={sortableColumns}
      items={[...filtered, { id: NEW_ITEM_ID }]}
      onRowClick={navigateRows ? doOnRowClick : undefined}
      scrollDisabled={!!editingId}
      toolBarItems={[
        <TextField
          key="search"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mr: 2 }}
        />,
        <AddButton key="add" />,
      ]}
    />
  )
}

function AddButton() {
  const disableAdd = useAtomValue(addingDisabledAtom)
  const [isAdding, setAdding] = useAtom(isAddingAtom)

  if (isAdding) {
    return <Button onClick={() => setAdding(false)}>Cancel Add</Button>
  }

  return (
    <Button disabled={isAdding || disableAdd} onClick={() => setAdding(true)}>
      Add
    </Button>
  )
}
